import React from "react";
import { View, Text, StyleSheet } from "@react-pdf/renderer";

// --- Color Palette ---
const colors = {
    primaryBlue: "#123460",
    lightGrayBg: "#F9FAFB",
    borderColor: "#D1D5DB",
    textLabel: "#4B5563",
    textValue: "#111827",
};

// --- Styles ---
const styles = StyleSheet.create({
    page: {
        padding: 20,
        // Add a bottom padding here to ensure it doesn't hit your absolute footer
        paddingBottom: 50,
        fontSize: 8,
        fontFamily: "Helvetica",
        backgroundColor: "#FFFFFF",
    },
    pdfSaveId: {
        fontSize: 4,
        color: "white",
        fontWeight: "normal",
        marginBottom: 5,
    },

    // -- Header --
    mainTitleContainer: {
        marginBottom: 10,
        borderBottomWidth: 1.5,
        borderBottomColor: colors.primaryBlue,
        paddingBottom: 6,
    },
    mainTitle: {
        fontSize: 14,
        color: colors.primaryBlue,
        fontFamily: "Helvetica-Bold",
        textTransform: "uppercase",
    },
    subTitleRow: {
        marginTop: 4,
        fontSize: 8,
        color: colors.textValue,
        lineHeight: 1.4,
    },

    // -- Section Headers --
    sectionHeaderRow: {
        flexDirection: "row",
        alignItems: "center",
        marginTop: 10,
        marginBottom: 6,
    },
    sectionTitleText: {
        fontSize: 9,
        color: colors.primaryBlue,
        fontFamily: "Helvetica-Bold",
        textTransform: "uppercase",
    },
    sectionLine: {
        flex: 1,
        height: 1,
        backgroundColor: colors.borderColor,
        marginLeft: 8,
    },

    // -- Key-Value Pairs Layout (Ultra Compact 3-Column Grid) --
    kvContainer: {
        flexDirection: "row",
        flexWrap: "wrap",
        borderTopWidth: 1,
        borderLeftWidth: 1,
        borderColor: colors.borderColor,
    },
    kvItem: {
        width: "33.33%",
        flexDirection: "row",
        borderBottomWidth: 1,
        borderRightWidth: 1,
        borderColor: colors.borderColor,
        alignItems: "stretch",
    },
    kvLabel: {
        width: "45%",
        backgroundColor: colors.lightGrayBg,
        paddingVertical: 3,
        paddingHorizontal: 4,
        color: colors.textLabel,
        fontSize: 6,
        fontFamily: "Helvetica-Bold",
        borderRightWidth: 1,
        borderRightColor: colors.borderColor,
    },
    kvValue: {
        width: "55%",
        paddingVertical: 3,
        paddingHorizontal: 4,
        color: colors.textValue,
        fontSize: 6,
    },

    // -- Table --
    table: {
        marginTop: 2,
        borderWidth: 1,
        borderColor: colors.borderColor,
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: colors.primaryBlue,
    },
    tableHeaderCell: {
        padding: 4,
        fontSize: 6,
        color: "#FFFFFF",
        fontFamily: "Helvetica-Bold",
        borderRightWidth: 1,
        borderRightColor: "#2A4B7C",
        textAlign: "center",
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: colors.borderColor,
    },
    tableCell: {
        padding: 4,
        fontSize: 6,
        color: colors.textValue,
        borderRightWidth: 1,
        borderRightColor: colors.borderColor,
        textAlign: "center",
        justifyContent: "center",
    },
    tableFooterRow: {
        flexDirection: "row",
        backgroundColor: colors.lightGrayBg,
        borderBottomWidth: 0,
    },
    tableFooterCell: {
        padding: 4,
        fontSize: 6,
        color: colors.primaryBlue,
        fontFamily: "Helvetica-Bold",
        borderRightWidth: 1,
        borderRightColor: colors.borderColor,
        textAlign: "center",
    },

    // Table Column Widths (Sum = 100%)
    colLabel: { width: "16%" },
    colRate: { width: "10%" },
    colFactors: { width: "16%" },
    colNet: { width: "14%" },
    colTax: { width: "10%" },
    colGrossLcl: { width: "17%" },
    colGrossUsd: { width: "17%", borderRightWidth: 0 },

    // -- Long Text Areas --
    longTextContainer: {
        marginTop: 2,
        borderWidth: 1,
        borderColor: colors.borderColor,
        backgroundColor: "#FCFDFD", // Very faint tint to distinguish from pure white
        padding: 6,
        borderRadius: 2,
    },
    longText: {
        fontSize: 7,
        color: colors.textValue,
        lineHeight: 1.5,
    },
});

const SectionTitle = ({ title }) => (
    <View style={styles.sectionHeaderRow} wrap={false}>
        <Text style={styles.sectionTitleText}>{title}</Text>
        <View style={styles.sectionLine} />
    </View>
);

const TariffPdfData = ({ tariffData, pdfId }) => {
    // Map fields for the 3-column grid
    const tariffFields = [
        { label: "Port", value: tariffData?.port?.portLabel },
        { label: "Country", value: tariffData?.country },
        { label: "Berth", value: tariffData?.berth },
        { label: "Cargo", value: tariffData?.cargo },
        { label: "Operation", value: tariffData?.operation },
        { label: "Cargo Quantity", value: tariffData?.cargo_quantity },
        { label: "GRT", value: tariffData?.grt },
        { label: "NRT", value: tariffData?.nrt },
        { label: "Duration (Days)", value: tariffData?.duration_in_days },
        { label: "Duration (Hours)", value: tariffData?.duration_in_hours },
        { label: "Local Currency", value: tariffData?.local_currency },
        { label: "Tariff Currency", value: tariffData?.tariff_rate_currency },
        { label: "Other Multiplier", value: tariffData?.other_multiplier },
        {
            label: "Exchange Rate",
            value: tariffData?.exchange_rate
                ? `${tariffData.exchange_rate} (${tariffData.tariff_rate_currency || 'USD'} to ${tariffData.local_currency || 'Local'})`
                : null
        },
    ];

    return (
        // Root View allows wrapping
        <View style={styles.page} wrap={true}>

            {pdfId && <Text style={styles.pdfSaveId} wrap={false}>tariffpdfId:{pdfId}#</Text>}

            {/* --- Main Header --- */}
            <View style={styles.mainTitleContainer} wrap={false}>
                <Text style={styles.mainTitle}>Port Tariff Details</Text>
                <View style={styles.subTitleRow}>
                    <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 9 }}>{tariffData?.tariff_name || "N/A"}</Text>
                    <Text style={{ marginTop: 2, color: colors.textLabel }}>{tariffData?.tariff_description || "No description provided."}</Text>
                </View>
            </View>

            {/* --- Tariff Parameters (3-Column KV) --- */}
            <SectionTitle title="Tariff Parameters" />
            <View style={styles.kvContainer} wrap={true}>
                {tariffFields.filter(f => f.value).map((field, index) => (
                    <View key={index} style={styles.kvItem} wrap={false}>
                        <Text style={styles.kvLabel}>{field.label}</Text>
                        <Text style={styles.kvValue}>{field.value}</Text>
                    </View>
                ))}
            </View>

            {/* --- Port Tariff Details Table --- */}
            <SectionTitle title="Tariff Calculation" />
            <View style={styles.table} wrap={true}>
                {/* Header */}
                <View style={styles.tableHeader} fixed>
                    <Text style={[styles.tableHeaderCell, styles.colLabel]}>Label</Text>
                    <Text style={[styles.tableHeaderCell, styles.colRate]}>Unit Rate</Text>
                    <Text style={[styles.tableHeaderCell, styles.colFactors]}>Factors</Text>
                    <Text style={[styles.tableHeaderCell, styles.colNet]}>Net ({tariffData?.local_currency || 'Local'})</Text>
                    <Text style={[styles.tableHeaderCell, styles.colTax]}>Tax (%)</Text>
                    <Text style={[styles.tableHeaderCell, styles.colGrossLcl]}>Gross ({tariffData?.local_currency || 'Local'})</Text>
                    <Text style={[styles.tableHeaderCell, styles.colGrossUsd]}>Gross ({tariffData?.tariff_rate_currency || 'USD'})</Text>
                </View>

                {/* Rows */}
                {tariffData?.parforma_port_tariff?.map((row, index) => (
                    <View key={index} style={[styles.tableRow, index % 2 !== 0 ? { backgroundColor: "#FAFBFC" } : {}]} wrap={false}>
                        <Text style={[styles.tableCell, styles.colLabel]}>{row?.label || "N/A"}</Text>
                        <Text style={[styles.tableCell, styles.colRate]}>{row?.unitRate || "0.00"}</Text>

                        <View style={[styles.tableCell, styles.colFactors, { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' }]}>
                            {row?.factors?.map((factor, fIndex) => (
                                <Text key={fIndex}>
                                    {factor}{fIndex !== row.factors.length - 1 ? " * " : ""}
                                </Text>
                            ))}
                        </View>

                        <Text style={[styles.tableCell, styles.colNet]}>{row?.netAmount?.toFixed(2) || "0.00"}</Text>
                        <Text style={[styles.tableCell, styles.colTax]}>{row?.tax || "0"}</Text>
                        <Text style={[styles.tableCell, styles.colGrossLcl]}>{row?.gross_local?.toFixed(2) || "0.00"}</Text>
                        <Text style={[styles.tableCell, styles.colGrossUsd]}>{row?.gross_tariff?.toFixed(2) || "0.00"}</Text>
                    </View>
                ))}

                {/* Footer Totals */}
                <View style={styles.tableFooterRow} wrap={false}>
                    <Text style={[styles.tableFooterCell, styles.colLabel, { borderRightWidth: 0 }]}></Text>
                    <Text style={[styles.tableFooterCell, styles.colRate, { borderRightWidth: 0 }]}></Text>
                    <Text style={[styles.tableFooterCell, styles.colFactors, { textAlign: 'right' }]}>Totals:</Text>
                    <Text style={[styles.tableFooterCell, styles.colNet]}>{tariffData?.net_amount?.toFixed(2) || "0.00"}</Text>
                    <Text style={[styles.tableFooterCell, styles.colTax]}></Text>
                    <Text style={[styles.tableFooterCell, styles.colGrossLcl]}>{tariffData?.total_gross?.toFixed(2) || "0.00"}</Text>
                    <Text style={[styles.tableFooterCell, styles.colGrossUsd]}>{tariffData?.total_exchange_rate?.toFixed(2) || "0.00"}</Text>
                </View>
            </View>

            {/* --- Tariff Structure Details (Long Text) --- */}
            {/* wrap={true} ensures that if the text is huge, it splits cleanly across pages instead of forcing a full page break */}
            <View wrap={true}>
                <SectionTitle title="Tariff Structure Details" />
                <View style={styles.longTextContainer} wrap={true}>
                    <Text style={styles.longText}>
                        {tariffData?.tariff_structure_details || "No details available"}
                    </Text>
                </View>
            </View>

            {/* --- Port Description (Long Text) --- */}
            <View wrap={true}>
                <SectionTitle title="Port Description" />
                <View style={styles.longTextContainer} wrap={true}>
                    <Text style={styles.longText}>
                        {tariffData?.port_description || "No port description available"}
                    </Text>
                </View>
            </View>

        </View>
    );
};

export default TariffPdfData;